import { WalletError } from "@solana/wallet-adapter-base";

import { phantomByteSigner } from "@/lib/wallet/phantom-standard";

import type { SolanaProvider } from "@/lib/mudwater";
import type { ByteSigner } from "@/lib/wallet/phantom-standard";
import type {
  Connection,
  SendOptions,
  VersionedTransaction,
} from "@solana/web3.js";

/**
 * The adapter, adapted to the SDK's provider contract.
 *
 * Thinner than `solana-adapter.ts`, which exists because Privy signs bytes and
 * so has to serialize and re-deserialize around every signature. Phantom takes
 * and returns a `VersionedTransaction`, which is exactly what the SDK's
 * `SolanaProvider` asks for, so there is nothing to convert.
 *
 * The adapter signs legacy and v0 only. Where Phantom's Wallet Standard
 * feature signs v1 (`phantom-standard.ts`), the provider also offers
 * `signTransactionBytes`, so the SDK prepares v1; otherwise it stays on v0.
 * Both are read when the SDK asks, since the account can change.
 *
 * Typed against the slice of the adapter it uses rather than the adapter
 * itself, the way `solana-adapter.ts` stays generic over its wallet, so this
 * can be exercised without an extension.
 */
export interface PhantomSigner {
  connect(): Promise<void>;
  signMessage(message: Uint8Array): Promise<Uint8Array>;
  signTransaction(
    transaction: VersionedTransaction
  ): Promise<VersionedTransaction>;
  sendTransaction(
    transaction: VersionedTransaction,
    connection: Connection,
    options?: SendOptions
  ): Promise<string>;
}

export function createPhantomProvider(input: {
  address: string;
  name?: string;
  advertisedTransactionVersions?: SolanaProvider["advertisedTransactionVersions"];
  telemetrySource?: SolanaProvider["telemetrySource"];
  adapter: PhantomSigner;
  bytes?: (address: string) => ByteSigner | null;
}): SolanaProvider {
  const { address, adapter, bytes = phantomByteSigner } = input;
  return {
    publicKey: { toBase58: () => address },
    name: input.name ?? "phantom",
    advertisedTransactionVersions: input.advertisedTransactionVersions,
    telemetrySource: input.telemetrySource ?? "phantom-adapter",
    get byteSigningTelemetry() {
      return {
        name: bytes === phantomByteSigner ? "phantom" : undefined,
        telemetrySource:
          bytes === phantomByteSigner
            ? ("wallet-standard" as const)
            : ("unknown" as const),
        advertisedTransactionVersions:
          bytes(address)?.advertisedTransactionVersions,
        supportedTransactionVersions:
          bytes(address)?.supportedTransactionVersions,
      };
    },
    connect: async () => {
      await adapter.connect();
      return address;
    },
    signMessage: (message) => adapter.signMessage(message),
    signTransaction: (transaction) => adapter.signTransaction(transaction),
    get supportedTransactionVersions() {
      return bytes(address)?.supportedTransactionVersions ?? [0];
    },
    signTransactionBytes: (transaction) => {
      const signer = bytes(address);
      if (!signer) throw new Error("Phantom cannot sign this transaction.");
      return signer.signTransactionBytes(transaction);
    },
    // Phantom's own signAndSendTransaction, for transactions only the holder signs.
    signAndSendTransaction: (transaction, connection) =>
      adapter.sendTransaction(transaction, connection, {
        preflightCommitment: "confirmed",
        maxRetries: 5,
      }),
  };
}

/**
 * Whether a thrown value is the holder declining in the wallet.
 *
 * The adapter wraps the wallet's own rejection, so the EIP-1193 `4001` the EVM
 * side matches on arrives nested in `error.cause`. `WalletError` carries that
 * original on `.error`, which is the shape the rest of the app never sees.
 */
export function isWalletRejection(error: unknown): boolean {
  if (!(error instanceof WalletError)) return false;
  const cause: unknown = error.error;
  const code: unknown =
    typeof cause === "object" && cause !== null
      ? Reflect.get(cause, "code")
      : undefined;
  return code === 4001 || /user rejected|user denied/i.test(error.message);
}
